<template>
    <Teleport to="body">
        <div v-if="isOpen" class="fixed inset-0 z-[61]" @click="emit('close')" @keyup.escape="emit('close')">
            <div
                ref="dropdownWrapper"
                :style="{
                    position: 'absolute',
                    top: `${top}px`,
                    left: `${left}px`,
                }"
                @click.stop
            >
                <slot />
            </div>
        </div>
    </Teleport>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref, useTemplateRef, watch } from 'vue'

type AlignmentStrategy = 'left' | 'middle'

type Props = {
    isOpen: boolean
    anchorRef: HTMLElement
    alignment: AlignmentStrategy
    viewportOffset?: number
}

const props = withDefaults(defineProps<Props>(), {
    viewportOffset: 20,
})

const emit = defineEmits<{
    close: []
}>()

const dropdownWrapper = useTemplateRef('dropdownWrapper')
const left = ref(0)
const top = ref(0)

onMounted(async () => {
    if (props.isOpen) {
        await nextTick()
        calculatePosition()
    }
})

async function calculatePosition() {
    const anchorElement = props.anchorRef
    if (!anchorElement) {
        return
    }

    const rect = anchorElement.getBoundingClientRect()
    const gap = 8 // Gap below anchor

    // Set top position from anchor's bottom edge
    top.value = rect.bottom + gap

    if (props.alignment === 'left') {
        // Simple left alignment - align dropdown's left edge with anchor's left edge
        left.value = rect.left
    } else if (props.alignment === 'middle') {
        // Wait for dropdown to render to get its actual width
        await nextTick()

        // Get the dropdown width dynamically
        const dropdownElement = dropdownWrapper.value?.firstElementChild as HTMLElement
        const dropdownWidth = dropdownElement?.offsetWidth || 580 // Fallback to w-[580px]
        const halfWidth = dropdownWidth / 2

        // Align middle of dropdown with anchor, but don't go past the viewport offset
        left.value = Math.max(rect.left - halfWidth, props.viewportOffset)
    }
}

// Recalculate position when dropdown opens or anchor changes
watch([() => props.isOpen, () => props.anchorRef, () => props.alignment], async () => {
    if (props.isOpen) {
        await nextTick()
        calculatePosition()
    }
})
</script>
